/**
 * Social Share Service
 * 
 * Gerencia compartilhamento para redes sociais via deep links
 * e tracking de menções para analytics de parceiros.
 */

import { db } from '@/core/services/firebase/config';
import { doc, increment, updateDoc, collection, addDoc, Timestamp } from 'firebase/firestore';

export interface ShareData {
  postId: string;
  userId: string;
  imageUrl: string;
  caption?: string;
  checkIn?: {
    tenantId: string;
    name: string;
    instagram?: string;
  };
  species?: string;
  weight?: number;
  location?: string;
  state?: string;
}

export type SharePlatform = 'instagram' | 'facebook' | 'whatsapp' | 'twitter';

/**
 * Gera caption formatada para redes sociais
 */
export function generateCaption(data: ShareData): string {
  const lines: string[] = [];
  
  // Linha principal (do post original ou genérica)
  if (data.caption) {
    lines.push(data.caption);
  }
  
  lines.push(''); // Linha vazia
  
  // Dados da captura
  if (data.species || data.weight) {
    let fishLine = '🐟';
    if (data.species) fishLine += ` ${data.species}`;
    if (data.weight) fishLine += ` - ${data.weight}kg`;
    lines.push(fishLine);
  }
  
  // Localização
  if (data.location || data.state) {
    lines.push(`📍 ${data.location || ''}${data.state ? ` - ${data.state}` : ''}`);
  }
  
  // Check-in com @
  if (data.checkIn) {
    const handle = data.checkIn.instagram 
      ? `@${data.checkIn.instagram.replace('@', '')}` 
      : data.checkIn.name;
    lines.push(`🏨 ${handle}`);
  }
  
  lines.push(''); // Linha vazia
  
  // Branding e hashtags
  lines.push('Registrado via @nossapesca 🎯');
  lines.push('#pesca #pescaesportiva #nossapesca #fishing');
  
  // Hashtag da espécie
  if (data.species) {
    const speciesTag = data.species
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '');
    lines.push(`#${speciesTag}`);
  }
  
  return lines.join('\n');
}

/**
 * Retorna sugestão de melhor horário para postar
 */
export function getBestTimeToPost(): { hour: number; message: string } {
  // Por enquanto, retorna horário fixo baseado em dados gerais
  // Futuramente pode usar analytics do usuário
  return {
    hour: 19,
    message: '💡 Melhor horário para postar: 19h',
  };
}

/**
 * Copia imagem para clipboard e abre Instagram
 */
export async function shareToInstagram(imageBlob: Blob, caption: string): Promise<void> {
  // Tenta copiar imagem para clipboard
  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        [imageBlob.type]: imageBlob,
      }),
    ]);
  } catch (err) {
    console.warn('Clipboard API não suportada, usando fallback');
    // Fallback: download da imagem
    downloadImage(imageBlob, 'nossapesca-share.jpg');
  }
  
  // Copia caption para clipboard separadamente
  try {
    await navigator.clipboard.writeText(caption);
  } catch (err) {
    console.warn('Erro ao copiar caption');
  }
  
  // Abre Instagram via deep link
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);
  
  if (isIOS || isAndroid) {
    // Mobile: abre app
    window.location.href = 'instagram://library';
    
    // Fallback depois de delay
    setTimeout(() => {
      window.open('https://instagram.com', '_blank');
    }, 2000);
  } else {
    // Desktop: abre web
    window.open('https://instagram.com', '_blank');
  }
}

/**
 * Copia imagem para clipboard e abre Facebook
 */
export async function shareToFacebook(imageBlob: Blob, caption: string): Promise<void> {
  // Tenta copiar imagem
  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        [imageBlob.type]: imageBlob,
      }),
    ]);
  } catch (err) {
    downloadImage(imageBlob, 'nossapesca-share.jpg');
  }
  
  // Facebook Web Share
  const shareUrl = `https://www.facebook.com/sharer/sharer.php?quote=${encodeURIComponent(caption)}`;
  window.open(shareUrl, '_blank', 'width=600,height=400');
}

/**
 * Compartilha via WhatsApp
 */
export function shareToWhatsApp(caption: string, imageUrl?: string): void {
  const text = imageUrl 
    ? `${caption}\n\n${imageUrl}`
    : caption;
  
  const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
  window.open(url, '_blank');
}

/**
 * Registra compartilhamento e incrementa contador de menções
 */
export async function trackShare(
  platform: SharePlatform,
  data: ShareData
): Promise<void> {
  try {
    // Salva registro do share
    await addDoc(collection(db, 'social_shares'), {
      postId: data.postId,
      userId: data.userId,
      platform,
      checkInTenantId: data.checkIn?.tenantId || null,
      sharedAt: Timestamp.now(),
    });
    
    // Incrementa contador de menções da pousada
    if (data.checkIn?.tenantId) {
      const tenantRef = doc(db, 'tenants', data.checkIn.tenantId);
      await updateDoc(tenantRef, {
        'portalProfile.mentionCount': increment(1),
      });
    }
  } catch (err) {
    console.error('Erro ao registrar share:', err);
  }
}

/**
 * Helper: faz download da imagem
 */
function downloadImage(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default {
  generateCaption,
  getBestTimeToPost,
  shareToInstagram,
  shareToFacebook,
  shareToWhatsApp,
  trackShare,
};
